import {
  type AcceptExchangeRateUpdateHook,
  EVM,
  type Route,
  createConfig,
  executeRoute,
} from "@lifi/sdk";
import { type EIP712TypedData } from "@umami/chains";
import { type Wallet } from "ethers";
import { type Chain, createWalletClient, http } from "viem";
import { etherlink, mainnet } from "viem/chains";

import { LIFI_CHAIN_ID_MAPPING } from "../config";
import { LiFiQuoteError } from "./lifiQuote";

// Chain mapping for LiFi execution
export const CHAIN_MAP: Record<number, Chain> = {
  1: mainnet,
  42793: etherlink, // Etherlink chain ID from LiFi mapping
};

// Initialize LiFi SDK configuration
let isConfigInitialized = false;

const initializeLiFiConfig = () => {
  if (isConfigInitialized) {
    return;
  }

  createConfig({
    integrator: "umami-wallet",
    providers: [
      EVM({
        getWalletClient: () => {
          // This will be set dynamically when executing routes
          throw new Error("Wallet client not initialized");
        },
        switchChain: _chainId => {
          // This will be handled by the execution function
          throw new Error("Chain switching not initialized");
        },
      }),
    ],
  });

  isConfigInitialized = true;
};

const createViemWalletClient = (wallet: Wallet, chainId: number) => {
  const chain = CHAIN_MAP[chainId];

  return createWalletClient({
    account: wallet.address as `0x${string}`,
    chain,
    transport: http(),
  }).extend(_client => ({
    async sendTransaction(transaction) {
      // Use the ethers.js wallet to sign and send the transaction
      const tx = await wallet.sendTransaction({
        to: transaction.to,
        value: transaction.value,
        data: transaction.data,
        gasLimit: transaction.gas,
        gasPrice: transaction.gasPrice,
        maxFeePerGas: transaction.maxFeePerGas,
        maxPriorityFeePerGas: transaction.maxPriorityFeePerGas,
        nonce: transaction.nonce,
      });

      return tx.hash as `0x${string}`;
    },
    async signMessage(message: string) {
      const signature = await wallet.signMessage(message);

      return signature as `0x${string}`;
    },
    async signTypedData(typedData: EIP712TypedData) {
      const signature = await wallet.signTypedData(
        typedData.domain,
        typedData.types,
        typedData.message
      );

      return signature as `0x${string}`;
    },
  }));
};

export interface LiFiExecutionParams {
  route: Route;
  wallet: Wallet;
  chainId: string;
  onRouteUpdate?: (route: Route) => void;
  onExchangeRateUpdate?: AcceptExchangeRateUpdateHook;
  onChainSwitch?: (targetChainId: number) => Promise<Wallet>;
}

export interface LiFiExecutionResult {
  status: "success" | "failed";
  route?: Route;
  error?: string;
}

export async function executeLiFiRoute({
  route,
  wallet,
  chainId,
  onRouteUpdate,
  onExchangeRateUpdate,
  onChainSwitch,
}: LiFiExecutionParams): Promise<LiFiExecutionResult> {
  try {
    // Initialize LiFi config if not already done
    initializeLiFiConfig();

    const numericChainId = parseInt(
      LIFI_CHAIN_ID_MAPPING[chainId as keyof typeof LIFI_CHAIN_ID_MAPPING]
    );

    if (isNaN(numericChainId)) {
      throw new LiFiQuoteError(`Unsupported chain: ${chainId}`);
    }

    // Create Viem wallet client from ethers.js wallet
    const walletClient = createViemWalletClient(wallet, numericChainId);

    // Create a new EVM provider with the wallet client
    const evmProvider = EVM({
      getWalletClient: async () => Promise.resolve(walletClient),
      switchChain: async targetChainId => {
        // If no chain switch handler is provided, throw an error
        if (!onChainSwitch) {
          throw new LiFiQuoteError(
            `Chain switching to ${targetChainId} is not supported. Please provide an onChainSwitch handler.`
          );
        }

        try {
          // Get a new wallet for the target chain
          const newWallet = await onChainSwitch(targetChainId);

          // Create a new wallet client for the target chain
          const newWalletClient = createViemWalletClient(newWallet, targetChainId);

          // Update the current wallet client reference
          Object.assign(walletClient, newWalletClient);

          return newWalletClient;
        } catch (error) {
          throw new LiFiQuoteError(
            `Failed to switch to chain ${targetChainId}: ${error instanceof Error ? error.message : "Unknown error"}`
          );
        }
      },
    });

    // Update the SDK configuration with the new provider
    const { config } = await import("@lifi/sdk");
    config.setProviders([evmProvider]);

    // Execute the route
    const executedRoute = await executeRoute(route, {
      updateRouteHook: onRouteUpdate,
      acceptExchangeRateUpdateHook: onExchangeRateUpdate,
      executeInBackground: false,
      disableMessageSigning: false,
    });

    return {
      status: "success",
      route: executedRoute,
    };
  } catch (error) {
    console.error("Error executing LiFi route:", error);

    if (error instanceof LiFiQuoteError) {
      return {
        status: "failed",
        error: error.message,
      };
    }

    return {
      status: "failed",
      error:
        error instanceof Error ? error.message : "Unknown error occurred during route execution",
    };
  }
}
